import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../../context/AuthContext';
import { emailSettingsService } from '../../services/api';
import toast from 'react-hot-toast';
import { Spinner } from '../../components/Spinner';
import { CheckCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

export const EmailIntegration = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery('emailSettings', emailSettingsService.getSettings, { retry: false });
  const settings = data?.data?.data || {};

  const [form, setForm] = useState({
    smtpHost: '', smtpPort: 587, smtpUser: '', smtpPass: '', fromEmail: '', fromName: '',
  });

  useEffect(() => {
    if (data) {
      setForm({
        smtpHost:  settings.smtpHost || '',
        smtpPort:  settings.smtpPort || 587,
        smtpUser:  settings.smtpUser || '',
        smtpPass:  settings.smtpPassConfigured ? '••••••••••••' : '',
        fromEmail: settings.fromEmail || '',
        fromName:  settings.fromName || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const saveMutation = useMutation(emailSettingsService.saveSettings, {
    onSuccess: () => { toast.success('Email settings saved'); queryClient.invalidateQueries('emailSettings'); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to save'),
  });

  const toggleActiveMutation = useMutation(
    (isActive) => emailSettingsService.saveSettings({ isActive }),
    {
      onSuccess: () => { toast.success('Updated'); queryClient.invalidateQueries('emailSettings'); },
      onError:   (e) => toast.error(e.response?.data?.message || 'Failed to update'),
    }
  );

  const testMutation = useMutation(emailSettingsService.sendTest, {
    onSuccess: (res) => toast.success(res.data?.message || 'Test email sent'),
    onError:   (e) => toast.error(e.response?.data?.message || 'Test failed'),
  });

  if (user?.role !== 'super_admin') {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
        Only the Super Admin of this workspace can configure email settings.
      </div>
    );
  }

  if (isLoading) return <div className="p-6 text-center"><Spinner size="md" /></div>;

  const handleSave = (e) => {
    e.preventDefault();
    saveMutation.mutate(form);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Email Notifications</h1>
        <p className="mt-1 text-sm text-gray-500">
          Connect an SMTP account so this workspace can email its Super Admin(s) when a lead can't be
          auto-distributed or fails to process — e.g. a Gmail account with an{' '}
          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-primary-600 underline">
            App Password
          </a>, or any other SMTP provider.
        </p>
      </div>

      {settings.isActive && (
        <div className="bg-green-50 border border-green-100 rounded-lg px-4 py-3 text-sm text-green-700 flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
          Email notifications are active for this workspace ({settings.smtpUser || 'account set'}).
        </div>
      )}

      <form onSubmit={handleSave} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <EnvelopeIcon className="h-5 w-5 text-gray-400" />
          SMTP Account
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">SMTP Host</label>
            <input
              value={form.smtpHost}
              onChange={e => setForm(f => ({ ...f, smtpHost: e.target.value }))}
              className="input text-sm w-full"
              placeholder="e.g. smtp.gmail.com"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">SMTP Port</label>
            <input
              type="number"
              value={form.smtpPort}
              onChange={e => setForm(f => ({ ...f, smtpPort: e.target.value }))}
              className="input text-sm w-full"
              placeholder="587"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">SMTP Username / Email</label>
            <input
              value={form.smtpUser}
              onChange={e => setForm(f => ({ ...f, smtpUser: e.target.value }))}
              className="input text-sm w-full"
              placeholder="e.g. alerts@yourcompany.com"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">SMTP Password</label>
            <input
              type="password"
              value={form.smtpPass}
              onChange={e => setForm(f => ({ ...f, smtpPass: e.target.value }))}
              className="input text-sm w-full"
              placeholder="Paste app password"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From Name (optional)</label>
            <input
              value={form.fromName}
              onChange={e => setForm(f => ({ ...f, fromName: e.target.value }))}
              className="input text-sm w-full"
              placeholder="e.g. DriveOnWheels Alerts"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From Email (optional)</label>
            <input
              value={form.fromEmail}
              onChange={e => setForm(f => ({ ...f, fromEmail: e.target.value }))}
              className="input text-sm w-full"
              placeholder="Defaults to SMTP username above"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-3">
            <button type="submit" disabled={saveMutation.isLoading} className="btn-primary text-sm">
              {saveMutation.isLoading ? <Spinner size="sm" /> : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isLoading}
              className="btn-outline-secondary text-sm"
            >
              {testMutation.isLoading ? <Spinner size="sm" /> : 'Send Test Email'}
            </button>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={!!settings.isActive}
              onChange={e => toggleActiveMutation.mutate(e.target.checked)}
              className="rounded"
            />
            Active
          </label>
        </div>
      </form>
    </div>
  );
};
